import { globalApiErrorHandler } from '../../../../Tanso/components/Utils/utils';
import { 
  extractCountry, 
  isRenewableTechnology, 
  findCIForTechnology, 
  applyTDFactor, 
  safeParseFloat,
  extractCountriesFromSalesData, 
  groupItemsByCountry, 
  calculateWeightedAverage, 
  initializeIntensityObject
} from './CommonUtils';
import { isSEBusinessSegment, convertEnergyValue } from './TotalVolumes';

// Define constants to avoid duplication
const REGION_GRID_PROPERTY = "Region | Grid";
const BUSINESS_SEGMENT_PROPERTY = "Business Segment";
const TECHNOLOGY_PROPERTY = "Technology";
const CATEGORY_PROPERTY = "Category";
const UNITS_PROPERTY = "Units";
const R4Q_PROPERTY = "R4Q";
const REGION_GRID_ALT_PROPERTY = "Region_Grid";
const WTT_POWER_CI_FIELD = "WtT Power CI (gCO2e/kWh)";
const WTT_POWER_CI_ALT_FIELD = "WtT_Power_CI";
const DEFAULT_MWH_UNITS = "MWh";
const TWH_UNITS = "TWh";
const GWH_UNITS = "GWh";
const TWH_TO_KWH = 1000000000;
const GWH_TO_KWH = 1000000;
const RENEWABLE_INTENSITY_PROPERTY = "renewableIntensity";

// Define interfaces for type safety
export interface RenewableDataItem {
  [REGION_GRID_PROPERTY]?: string;
  [REGION_GRID_ALT_PROPERTY]?: string;
  [TECHNOLOGY_PROPERTY]?: string;
  [BUSINESS_SEGMENT_PROPERTY]?: string;
  [UNITS_PROPERTY]?: string;
  [R4Q_PROPERTY]?: string | number;
}

export interface TechIntensityItem {
  [CATEGORY_PROPERTY]?: string;
  [TECHNOLOGY_PROPERTY]?: string;
  [WTT_POWER_CI_FIELD]?: string | number;
  [WTT_POWER_CI_ALT_FIELD]?: string | number;
  [key: string]: string | number | undefined;
}

export interface TechIntensityMap {
  [key: string]: number;
}

export interface GridBusinessSegmentMap {
  [gridRegion: string]: string | undefined;
}

export interface ProcessedRenewableItem extends RenewableDataItem {
  country: string;
  volume: number;
  volumeInKwh: number;
  ci: number | null;
  rawCI: number | null;
  isSE: boolean;
  businessSegment: string;
  units: string;
}

export interface ErrorItem {
  error: boolean;
  message: string;
  originalItem: RenewableDataItem | null;
  country: string;
}

export type ProcessedItem = ProcessedRenewableItem | ErrorItem;

export interface CountryItems {
  [country: string]: ProcessedItem[];
}

export interface CalculationDetails {
  items: ProcessedItem[];
  units: string;
}

export interface RenewableIntensity {
  [RENEWABLE_INTENSITY_PROPERTY]: number;
  isSE: boolean;
  calculationDetails: CalculationDetails;
}

export interface RenewableIntensities {
  [country: string]: RenewableIntensity;
}

/**
 * Calculate renewable intensities by country
 * @param renewableCertificatesData - Renewable certificates data
 * @param techIntensitiesData - Technology intensities data
 * @param threePGenTechSpecificData - 3P gen tech specific data
 * @param total3PGenSalesData - Total 3P gen sales data
 * @param tdFactorsData - T&D factors data
 * @returns Renewable intensities by country or error
 */
export function calculateRenewableIntensities(
  renewableCertificatesData: RenewableDataItem[], 
  techIntensitiesData: TechIntensityItem[], 
  threePGenTechSpecificData: RenewableDataItem[],
  total3PGenSalesData: RenewableDataItem[] = [],
  tdFactorsData: RenewableDataItem[] = []
): RenewableIntensities | Error {
  try {
    // Validate tdFactorsData to prevent errors
    if (!Array.isArray(tdFactorsData)) {
      tdFactorsData = [];
    }
    
    // Extract renewable tech items from 3P generation data
    const renewableTechItems = extractRenewableTechItems(threePGenTechSpecificData);
    
    // Combine with renewable certificates data
    const normalizedRenewableCertificatesData = [
      ...(Array.isArray(renewableCertificatesData) ? renewableCertificatesData : [])
        .map(item => normalizeRenewableDataFields(item)),
      ...renewableTechItems
    ];
    
    // Create technology intensity map
    const techIntensityMap = createTechIntensityMap(techIntensitiesData);
    
    // Create a business segment lookup for each grid region
    const gridBusinessSegmentMap = createGridBusinessSegmentMap(total3PGenSalesData);
    
    // Process each renewable certificate item with better error handling
    const processedItems: ProcessedItem[] = [];
    
    for (const item of normalizedRenewableCertificatesData) {
      try {
        const processedItem = processRenewableCertificateItem(
          item, 
          techIntensityMap, 
          techIntensitiesData, 
          tdFactorsData, 
          gridBusinessSegmentMap
        );
        processedItems.push(processedItem);
      } catch (itemError) {
        globalApiErrorHandler("Error processing item", itemError)
      }
    }
    
    // Extract countries from sales data
    const countries = extractCountriesFromSalesData(total3PGenSalesData);
    
    // Group processed items by country
// Replace the current line with this:
const countryCertData = groupItemsByCountry(processedItems) as Record<string, ProcessedItem[]>;    
    // Initialize renewable intensities for all countries
    const renewableIntensities = initializeIntensityObject(countries, RENEWABLE_INTENSITY_PROPERTY) as RenewableIntensities;
    
    // Calculate weighted average for countries with data
    Object.keys(countryCertData).forEach(country => {
      const countryItems = countryCertData[country];
      const weightedAverage = calculateWeightedAverage(countryItems);
      
      // Check if any item in this country is from SE business segment
      const isSE = countryItems.some((item: ProcessedItem) => (item as ProcessedRenewableItem).isSE === true);
      
      renewableIntensities[country] = {
        [RENEWABLE_INTENSITY_PROPERTY]: weightedAverage,
        isSE,
        calculationDetails: {
          items: countryItems,
          units: isSE ? TWH_UNITS : GWH_UNITS
        }
      };
    });
    
    return renewableIntensities;
  } catch (error) {
    return error instanceof Error ? error : new Error("Unknown error occurred");
  }
}

/**
 * Create a lookup map for business segments by grid region
 * @param total3PGenSalesData - Total 3P gen sales data
 * @returns Lookup map for business segments by grid region
 */
function createGridBusinessSegmentMap(
  total3PGenSalesData: RenewableDataItem[]
): GridBusinessSegmentMap {
  const gridBusinessSegmentMap: GridBusinessSegmentMap = {};
  
  if (!Array.isArray(total3PGenSalesData)) {
    return gridBusinessSegmentMap;
  }
  
  total3PGenSalesData.forEach(item => {
    const gridRegion = item[REGION_GRID_PROPERTY];
    if (!gridRegion) {
      return;
    }
    
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY];
    gridBusinessSegmentMap[gridRegion] = businessSegment;
  });
  
  return gridBusinessSegmentMap;
}

/**
 * Create a lookup map for technology intensities
 * @param techIntensitiesData - Technology intensity data array
 * @returns Lookup map for technology intensities
 */
function createTechIntensityMap(techIntensitiesData: TechIntensityItem[]): TechIntensityMap {
  const techIntensityMap: TechIntensityMap = {};
  
  if (!Array.isArray(techIntensitiesData)) {
    return techIntensityMap;
  }
  
  techIntensitiesData.forEach(item => {
    if (!item[CATEGORY_PROPERTY] || !item[TECHNOLOGY_PROPERTY]) {
      return;
    }
    
    // Store with lowercase keys for case-insensitive matching
    const key = `${item[CATEGORY_PROPERTY].toLowerCase()} | ${item[TECHNOLOGY_PROPERTY].toLowerCase()}`;
    const intensityValue = item[WTT_POWER_CI_FIELD] ?? item[WTT_POWER_CI_ALT_FIELD];
    techIntensityMap[key] = safeParseFloat(String(intensityValue));
    
    // Also store the individual components for easier partial matching
    techIntensityMap[(item[CATEGORY_PROPERTY] ?? '').toLowerCase()] = safeParseFloat(String(intensityValue));
    techIntensityMap[item[TECHNOLOGY_PROPERTY].toLowerCase()] = safeParseFloat(String(intensityValue));
  });
  
  return techIntensityMap;
}

/**
 * Normalize renewable data object field names
 * @param item - Data object to normalize
 * @returns Normalized data object
 */
export function normalizeRenewableDataFields(
  item: RenewableDataItem
): RenewableDataItem {
  if (!item) {
    return {};
  }
  
  return {
    ...item,
    [REGION_GRID_PROPERTY]: item[REGION_GRID_PROPERTY] ?? item[REGION_GRID_ALT_PROPERTY],
    [TECHNOLOGY_PROPERTY]: item[TECHNOLOGY_PROPERTY],
    [BUSINESS_SEGMENT_PROPERTY]: item[BUSINESS_SEGMENT_PROPERTY] ?? ""
  };
}

/**
 * Extract renewable tech items from 3P generation data
 * @param threePGenTechSpecificData - 3P generation data
 * @returns Renewable tech items
 */
export function extractRenewableTechItems(
  threePGenTechSpecificData: RenewableDataItem[]
): RenewableDataItem[] {
  if (!Array.isArray(threePGenTechSpecificData)) {
    return [];
  }
  
  return threePGenTechSpecificData
    .filter(item => isRenewableTechnology(item[TECHNOLOGY_PROPERTY] ?? ''))
    .map(item => normalizeRenewableDataFields(item));
}


/**
 * Convert energy volume to kWh based on business segment
 */
function convertVolumeBySegment(
  r4q: number, 
  inputUnits: string, 
  isSE: boolean
): { volume: number; volumeInKwh: number } {
  if (isSE) {
    const seVolume = convertEnergyValue(r4q, inputUnits, TWH_UNITS);
    return { volume: seVolume, volumeInKwh: seVolume * TWH_TO_KWH };
  }
  
  const volume = convertEnergyValue(r4q, inputUnits, GWH_UNITS);
  return { volume, volumeInKwh: volume * GWH_TO_KWH };
}

/**
 * Apply T&D factor safely with proper error handling
 */
function applySafeTDFactor(
  rawCI: number | null, 
  country: string, 
  tdFactorsData: RenewableDataItem[]
): { ci: number | null; error?: { message: string } } {
  // Default to rawCI
  const isInvalidCountry = !country;
  const isInvalidRawCI = !rawCI || isNaN(Number(rawCI));
  const isInvalidTdFactors = !Array.isArray(tdFactorsData) || tdFactorsData.length === 0;

  if (isInvalidCountry || isInvalidRawCI || isInvalidTdFactors) {
    return { ci: rawCI };
  }
  
  try {
    return { ci: applyTDFactor(rawCI, true, country, tdFactorsData) };
  } catch (tdError) {
    return { 
      ci: rawCI,
      error: { message: tdError instanceof Error ? tdError.message : "Error applying T&D factor" }
    };
  }
}

/**
 * Process a renewable certificate item to calculate CI
 * @param item - Certificate item to process
 * @param techIntensityMap - Technology intensity map
 * @param techIntensitiesData - Technology intensity data
 * @param tdFactorsData - T&D factors data
 * @param gridBusinessSegmentMap - Grid business segment map
 * @returns Processed item with CI and volume
 */
export function processRenewableCertificateItem(
  item: RenewableDataItem, 
  techIntensityMap: TechIntensityMap, 
  techIntensitiesData: TechIntensityItem[], 
  tdFactorsData: RenewableDataItem[],
  gridBusinessSegmentMap: GridBusinessSegmentMap = {}
): ProcessedItem {
  try {
    if (!item) {
      throw new Error("Item is null or undefined");
    }
    
    // Parse the R4Q value
    const r4q = safeParseFloat(String(item[R4Q_PROPERTY] ?? ""));
    
    // Get basic item properties
    const gridRegion = item[REGION_GRID_PROPERTY] ?? '';
    const country = extractCountry(gridRegion);
    const businessSegment = item[BUSINESS_SEGMENT_PROPERTY] ?? gridBusinessSegmentMap[gridRegion] ?? "";
    const isSE = isSEBusinessSegment(businessSegment);
    const inputUnits = item[UNITS_PROPERTY] ?? DEFAULT_MWH_UNITS;
    
    // Convert volume units based on business segment
    const { volume, volumeInKwh } = convertVolumeBySegment(r4q, inputUnits, isSE);
    
    // Get carbon intensity value
    const technology = item[TECHNOLOGY_PROPERTY];
    const rawCI = technology ? findCIForTechnology(technology, techIntensityMap, techIntensitiesData) : null;
    
    // Apply T&D factor with error handling
    const tdResult = applySafeTDFactor(rawCI, country, tdFactorsData);
    
    // If there was an error applying T&D factor, return an error object
    if (tdResult.error) {
      return {
        error: true,
        message: tdResult.error.message,
        originalItem: item,
        country: country ?? 'unknown'
      };
    }
    
    // Return the processed item
    return {
      ...item,
      country,
      volume,
      volumeInKwh,
      ci: tdResult.ci,
      rawCI,
      isSE,
      businessSegment,
      units: isSE ? TWH_UNITS : GWH_UNITS
    };
  } catch (error) {
    // Return a minimal object with error information
    return {
      error: true,
      message: error instanceof Error ? error.message : "Unknown error processing item",
      originalItem: item,
      country: extractCountry(item?.[REGION_GRID_PROPERTY] ?? '') ?? 'unknown'
    };
  }
}